import React from 'react';
import moment from "moment";
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';

import {
  Column,
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  usePagination,
  useSortBy,
} from 'react-table';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/solid';
import { FieldInput } from '../fieldInput';
import { defaultPageSize, defaultPageSizeOptions } from '../../common/constants';

type TableProps = {
  columns: Array<Column>;
  data: Array<any>;
  initialState?: any;
  showPagination?: Boolean;
  skipFilter?: Boolean;
  getRowProps?: any;
  postFilter?: JSX.Element;
  resetOnDataChange?: boolean;
  onChangePageSize?: Function;
  customPageSize?: number;
};

const getTimeFromFormattedTotal = (total: string): number => {
  const totalSplit = total.split(' ');
  const h = Math.floor(moment.duration(totalSplit[0].slice(0, -1), 'hours').asMinutes());
  const m = Math.floor(moment.duration(totalSplit[1].slice(0, -1), 'minutes').asMinutes());
  return h + m;
}

const DataTable = ({
  columns,
  data,
  initialState,
  showPagination,
  getRowProps,
  skipFilter,
  postFilter = <></>,
  resetOnDataChange = true,
  onChangePageSize = undefined,
  customPageSize = undefined
}: TableProps) => {
  //var defaultInitialState = { pageSize: showPagination ? 30 : customPageSize && customPageSize > 100 ? customPageSize : (data && data.length ? data.length : defaultPageSizeOptions[0]) };
  var defaultInitialState = { pageSize: showPagination ? 30 : (data && data.length ? data.length : defaultPageSize) };
  initialState = (initialState === undefined) ? defaultInitialState : { ...defaultInitialState, ...initialState };

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState,
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => {
          var rowOneColumn = row1.values[columnName];
          var rowTwoColumn = row2.values[columnName];

          if (initialState.hasOwnProperty("sortType") && initialState.sortType == "ci") {
            try {
              rowOneColumn = rowOneColumn.toLowerCase();
              rowTwoColumn = rowTwoColumn.toLowerCase();
            } catch (e) {

            }
          }

          if (isNaN(rowOneColumn)) {
            if (columnName == 'Total') {
              try {
                return Number(getTimeFromFormattedTotal(rowOneColumn)) > Number(getTimeFromFormattedTotal(rowTwoColumn)) ? 1 : -1;
              } catch (e) {

              }
            }
            else {
              return rowOneColumn > rowTwoColumn ? 1 : -1;
            }
          }

          return Number(rowOneColumn) > Number(rowTwoColumn) ? 1 : -1;
        },
      },
      autoResetPage: resetOnDataChange,
      autoResetExpanded: resetOnDataChange,
      autoResetGroupBy: resetOnDataChange,
      autoResetSelectedRows: resetOnDataChange,
      autoResetFilters: resetOnDataChange,
      autoResetRowState: resetOnDataChange,
      autoResetSortBy: resetOnDataChange,
      autoResetGlobalFilter: resetOnDataChange,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  /**
   * Changes the page size and emits an event if one is passed into the component
   * @param pageSize number
   */
  const changePageSize = (pageSize: number) => {
    setPageSize(pageSize);
    setTimeout(() => onChangePageSize && onChangePageSize(pageSize), 1);
  }

  /**
   * Render function
   */
  return (
    <div className="dataTable">
      <div className='hidden md:flex gap-x-2'>
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) =>
            column.Filter ? (
              <div key={column.id}>{column.render('Filter')}</div>
            ) : null
          )
        )}
      </div>

      {!skipFilter && (
        <div>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      )}

      {postFilter}

      <div className='flex flex-col mt-2'>
        <div className='-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
          <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
            <div className='overflow-hidden border-b border-gray-200 shadow'>
              <table
                {...getTableProps()}
                className='min-w-full divide-y divide-gray-200'
              >
                <thead className='bg-ptstableheader'>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({ title: undefined })
                          )}
                          {...column.getHeaderProps({
                            style: { width: column.width },
                          })}
                          scope='col'
                          className={classNames(
                            'group px-3 py-3 text-left text-xs text-black uppercase tracking-wider h-12',
                            column.className ? column.className : ''
                          )}
                        >
                          <div className='flex items-center justify-between'>
                            {column.render('Header')}
                            {/* Add a sort direction indicator */}
                            <span>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <SortDownIcon className='w-4 h-4 text-black' />
                                ) : (
                                  <SortUpIcon className='w-4 h-4 text-black' />
                                )
                              ) : column.canSort ? (
                                <SortIcon className='w-4 h-4 text-black opacity-0 group-hover:opacity-100' />
                              ) : (
                                ''
                              )}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className='bg-white divide-y divide-gray-200'
                >
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps(
                          getRowProps ? getRowProps(row) : ''
                        )}
                      >
                        {row.cells.map((cell: any) => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className={classNames(
                                'px-3 py-3',
                                cell.column.className
                                  ? cell.column.className
                                  : ''
                              )}
                              {...cell.getCellProps({
                                style: {
                                  width: cell.column.width,
                                },
                              })}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showPagination && (
        <div className='flex items-center justify-between py-3'>
          <div className='flex justify-between flex-1 sm:hidden'>
            <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
              Previous
            </Button>
            <Button onClick={() => nextPage()} disabled={!canNextPage}>
              Next
            </Button>
          </div>
          <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
            <div className='flex items-end gap-x-2'>
              <span className='w-32 text-sm text-gray-700'>
                Page <span className='font-medium'>{state.pageIndex + 1}</span>{' '}
                of <span className='font-medium'>{pageOptions.length}</span>
              </span>
              <Select
                className='inline-block w-40'
                value={{ value: state.pageSize, label: `Show ${state.pageSize}` }}
                onChange={(selected) => selected && changePageSize(Number(selected.value))}
                options={defaultPageSizeOptions.map((pageSize) => ({
                  value: pageSize,
                  label: `Show ${pageSize}`,
                }))}
                styles={{
                  ...graySelectStyles,
                  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                }}
                menuPortalTarget={document.body}
                isSearchable={false}
              />
            </div>
            <div>
              <nav
                className='relative z-0 inline-flex -space-x-px rounded-md shadow-sm'
                aria-label='Pagination'
              >
                <PageButton
                  className='rounded-l-md'
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                >
                  <span className='sr-only'>First</span>
                  <ChevronDoubleLeftIcon
                    className='w-5 h-5'
                    aria-hidden='true'
                  />
                </PageButton>
                <PageButton
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  <span className='sr-only'>Previous</span>
                  <ChevronLeftIcon className='w-5 h-5' aria-hidden='true' />
                </PageButton>
                <PageButton onClick={() => nextPage()} disabled={!canNextPage}>
                  <span className='sr-only'>Next</span>
                  <ChevronRightIcon className='w-5 h-5' aria-hidden='true' />
                </PageButton>
                <PageButton
                  className='rounded-r-md'
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                >
                  <span className='sr-only'>Last</span>
                  <ChevronDoubleRightIcon
                    className='w-5 h-5'
                    aria-hidden='true'
                  />
                </PageButton>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}: any) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <label className='w-full'>
      <FieldInput
        label='Filter:'
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        value={value}
        placeholder={`${count} records...`}
      />
    </label>
  );
}
export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id, render },
}: any) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row: any) => {
      var splitValues = row.values[id].split(',');
      splitValues.forEach((d: any) => {
        if (!options.has(d)) options.add(d);
      });
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <div className='flex items-baseline gap-x-2'>
      <label>{render('Header')}: </label>
      <label className='flex items-baseline gap-x-2'>
        <Select
          className='block w-full mt-1'
          value={filterValue ? { value: filterValue, label: filterValue } : { value: '', label: 'All' }}
          onChange={(selected) => {
            setFilter(selected && selected.value !== '' ? selected.value : undefined);
          }}
          options={[
            { value: '', label: 'All' },
            ...options.sort((a, b) => {
              if ((typeof a === 'string') && (typeof b === 'string')) {
                return a.localeCompare(b);
              }
              return 0;
            }).map((option: any) => ({
              value: option,
              label: option,
            }))
          ]}
          styles={graySelectStyles}
          isSearchable={false}
        />
      </label>
    </div>
  );
}
export function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}

export function Button({ children, className, ...rest }: any) {
  return (
    <button
      type='button'
      className={classNames(
        'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50',
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function PageButton({ children, className, ...rest }: any) {
  return (
    <button
      type='button'
      className={classNames(
        'relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50',
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function SortIcon({ className }: any) {
  return (
    <svg
      className={className}
      stroke='currentColor'
      fill='currentColor'
      strokeWidth='0'
      viewBox='0 0 320 512'
      height='1em'
      width='1em'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d='M41 288h238c21.4 0 32.1 25.9 17 41L177 448c-9.4 9.4-24.6 9.4-33.9 0L24 329c-15.1-15.1-4.4-41 17-41zm255-105L177 64c-9.4-9.4-24.6-9.4-33.9 0L24 183c-15.1 15.1-4.4 41 17 41h238c21.4 0 32.1-25.9 17-41z'></path>
    </svg>
  );
}

export function SortUpIcon({ className }: any) {
  return (
    <svg
      className={className}
      stroke='currentColor'
      fill='currentColor'
      strokeWidth='0'
      viewBox='0 0 320 512'
      height='1em'
      width='1em'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d='M279 224H41c-21.4 0-32.1-25.9-17-41L143 64c9.4-9.4 24.6-9.4 33.9 0l119 119c15.2 15.1 4.5 41-16.9 41z'></path>
    </svg>
  );
}

export function SortDownIcon({ className }: any) {
  return (
    <svg
      className={className}
      stroke='currentColor'
      fill='currentColor'
      strokeWidth='0'
      viewBox='0 0 320 512'
      height='1em'
      width='1em'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d='M41 288h238c21.4 0 32.1 25.9 17 41L177 448c-9.4 9.4-24.6 9.4-33.9 0L24 329c-15.1-15.1-4.4-41 17-41z'></path>
    </svg>
  );
}